import type { AppRouter } from "@quake/fusion/dashboard-trpc";
import type { StreamsRouter } from "@quake/fusion/streams-trpc";
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  HTTPBatchLinkOptions,
  HTTPLinkOptions,
  httpBatchStreamLink,
  splitLink,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { getQueryClient } from "./queryClient";

export const queryClient = getQueryClient();

const trpcFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  const contentType = response.headers.get("content-type")?.toLowerCase();

  if (contentType?.includes("json")) {
    return response;
  }

  // Gateways can return HTML errors, which tRPC would otherwise report as JSON parsing failures.
  if (response.status === 504) {
    throw new Error("The request timed out. Check current state before retrying.");
  }

  if (response.status === 502 || response.status === 503) {
    throw new Error("The service is temporarily unavailable. Check current state before retrying.");
  }

  return response;
};

const options = {
  url: "/trpc",
  maxURLLength: 2083,
  fetch: trpcFetch,
  headers: () => {
    const slug = window.location.pathname.split("/")[1];
    if (!slug) return {};
    const blocklist = ["verify-user-dataset", "verify", "select-dataset", "login", "openapi-oauth", "mfa"];
    if (blocklist.includes(slug)) {
      return {};
    }
    return {
      "x-dataset-slug": slug,
    };
  },
} satisfies HTTPBatchLinkOptions<any> | HTTPLinkOptions<any>;

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    import.meta.env.PROD
      ? splitLink({
          false: httpBatchLink(options),
          true: httpLink(options),
          condition: (operation) => {
            return (
              operation.path.includes("structuredSearch") ||
              operation.path === "syncs.previewSourceObject" ||
              operation.path === "pdfEditor.renderHtml" ||
              operation.path.includes("data.") ||
              operation.path.includes("emailLogs.")
            );
          },
        })
      : httpLink(options),
  ],
});

export const streamsTrpcClient = createTRPCClient<StreamsRouter>({
  links: [httpBatchStreamLink({ ...options, url: "/stream-api" })],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});

export const streamsTrpc = createTRPCOptionsProxy<StreamsRouter>({
  client: streamsTrpcClient,
  queryClient,
});
